import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { TriggerAgentRequest } from '@melio/risk-ai-agents-ts-client-config';
import { AgentExecutionType, AgentResultResponse } from '@tools/ui/types/generated/graphql';
import { GET_RISK_AGENT_RESPONSE, TRIGGER_RISK_AGENT } from '../graphql/mutations';
import { TriggerItem } from './TriggeredStatus';

const WAITING_MESSAGES = [
  'Connecting the dots…',
  'Still on it — complex one…',
  'Deep dive in progress…',
  'Crunching the numbers…',
  'Your patience is appreciated…',
  'Pulling it all together…',
  'Working through the data…',
  'Houston, we have a… process running.',
  'May the data be with you!',
  'To infinity and… your results.',
  'One does not simply rush an AI.',
  'In a galaxy not so far away… your data awaits.',
  'Life is like a box of data points. Sorting them now.',
  'With great data comes great responsibility. Almost ready.',
  "I'll be back... with your results.",
  "Almost there. The AI promised it won't ghost you like that vendor from last quarter.",
  "Analyzing data at the speed of... well, not light, but... it's pretty close.",
  "Make love not fraud.",
  "Lets put the fun in fund!",
];

export const POLL_INTERVAL_MS = 5000;
const TERMINAL_STATUSES = ['done', 'failed'];

type UseRiskAgentRunnerParams = {
  agentName: string | undefined;
  entityId: string;
  entityType: TriggerAgentRequest['entity']['type'];
  executionType: AgentExecutionType;
  orchestrationExecutionId: string;
  partnerName: string;
  reason: string;
};

const useRiskAgentRunner = ({
  agentName,
  entityId,
  entityType,
  executionType,
  orchestrationExecutionId,
  partnerName,
  reason,
}: UseRiskAgentRunnerParams) => {
  const [triggerRiskAgent] = useMutation(TRIGGER_RISK_AGENT);

  const [isTriggering, setIsTriggering] = useState(false);
  const [triggered, setTriggered] = useState<TriggerItem[]>([]);
  const [results, setResults] = useState<AgentResultResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [waitingMessage, setWaitingMessage] = useState(WAITING_MESSAGES[0]);

  const isMountedRef = useRef(true);
  const isPollingActiveRef = useRef(false);

  const { loading: isFetching } = useQuery(GET_RISK_AGENT_RESPONSE, {
    variables: { agentNames: agentName ? [agentName] : [], entityId, entityType },
    skip: !isPolling,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    pollInterval: POLL_INTERVAL_MS,
    onCompleted: (data: { getAgentResponse?: AgentResultResponse[] | null }) => {
      if (!isMountedRef.current || !isPollingActiveRef.current) return;
      const agentResults: AgentResultResponse[] = data?.getAgentResponse ?? [];
      setResults(agentResults);
      setPollAttempts((prev) => prev + 1);
      if (agentResults.length > 0 && agentResults.every((r) => TERMINAL_STATUSES.includes(r.status))) {
        isPollingActiveRef.current = false;
        setIsPolling(false);
      }
    },
    onError: (err: Error) => {
      if (!isMountedRef.current || !isPollingActiveRef.current) return;
      setError(err.message);
      isPollingActiveRef.current = false;
      setIsPolling(false);
    },
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isPolling) return;
    const interval = setInterval(() => {
      setWaitingMessage(WAITING_MESSAGES[Math.floor(Math.random() * WAITING_MESSAGES.length)]);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isPolling]);

  const trigger = async () => {
    if (!agentName || !entityId.trim() || !reason.trim()) return;

    setIsTriggering(true);
    setError(null);
    setResults([]);
    setTriggered([]);
    setIsPolling(false);
    setPollAttempts(0);

    try {
      const { data } = await triggerRiskAgent({
        variables: {
          agentNames: [agentName],
          entityId: entityId.trim(),
          entityType,
          reason: reason.trim(),
          executionType,
          orchestrationExecutionId: orchestrationExecutionId.trim() || undefined,
          partnerName: partnerName || undefined,
        },
      });

      const items: TriggerItem[] = data?.triggerRiskAgent ?? [];
      if (!isMountedRef.current) return;
      setTriggered(items);
      setIsTriggering(false);

      if (!items.length) return;

      isPollingActiveRef.current = true;
      setIsPolling(true);
    } catch (err: any) {
      if (!isMountedRef.current) return;
      const msg = err?.graphQLErrors?.[0]?.message ?? err?.message ?? String(err);
      setError(msg);
    } finally {
      if (isMountedRef.current) setIsTriggering(false);
    }
  };

  const reset = () => {
    isPollingActiveRef.current = false;
    setTriggered([]);
    setResults([]);
    setError(null);
    setIsPolling(false);
    setPollAttempts(0);
  };

  return { error, isFetching, isPolling, isTriggering, pollAttempts, results, triggered, trigger, reset, waitingMessage };
};

export { useRiskAgentRunner };
